import styled, { css } from 'styled-components';
import { Text } from '@lmn/design-system';
import { useTranslation } from '@/app/components/translations/Translation';
import { formatCancellationDate, getCancellationBeforeDate } from '@/app/components/hotel-detail/common/dates';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import { Country, Language } from '@/domainLogic/index.ts';
import { formatPrice } from '@/app/components/search/utils/formatting.ts';
import { Icon } from '@/app/common/icons/Icon';
import {
  buildCancellationPoliciesByRange,
  cancellationPoliciesTranslationKeys,
  groupRefundableByUpToDate,
} from '../../rooms/cancellationPolicies';
import { getPromotionDisplayLabel } from '../../rooms/promotionLabels';
import { useUrlSearchParams } from '../../UrlSearchParamsProvider';

// Timeline connector between two bullets. Zero-width + border-left so it centres on the dot (the
// column centres it), and a small negative bottom margin bridges the 4px row gap so it reaches the
// next bullet without touching it.
const VerticalDottedLine = styled.div`
  width: 0;
  flex-grow: 1;
  border-left: 2px dashed ${({ theme }) => theme.kallisto.color.border.static.borderNeutral};
  margin-bottom: -4px;
`;

const Separator = styled.div`
  width: 100%;
  height: 1px;
  margin: 16px 0;
  background-color: ${({ theme }) => theme.kallisto.color.border.static.borderNeutral};
`;

// Full-width divider between top-level cancellation sections (non-refundable / refundable / flight).
const SectionSeparator = styled.div`
  width: 100%;
  height: 1px;
  margin: 0 0 24px;
  background-color: ${({ theme }) => theme.kallisto.color.border.static.borderNeutral};
`;

// The gap below a section is only there to separate it from the next one — the last section leaves
// the spacing under it to the modal body's own bottom padding, or the explainer ends up sitting in
// noticeably more empty space than the other modals (MPH-1116).
const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const CancellationDatesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  &:last-child {
    ${Separator} {
      display: none;
    }
  }
`;

const CancellationDateInfo = styled.div`
  display: flex;
  gap: 8px;
`;

// Flush horizontally: the explainer sits inside containers that already provide the side inset
// (the modal body padding, or the "Cancellation options" disclosure whose options are flush too).
const InformationContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

// Timeline bullet marker (8px dot centred in a 20px box) replacing the old calendar icon.
const TimelineDot = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 20px;
  height: 20px;

  &::before {
    content: '';
    display: block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ theme }) => theme.kallisto.color.icon.static.iconPrimary};
  }
`;

export function EvoRoomCancellationInformation({ mealPlans }: { mealPlans: readonly MealPlanType[] }) {
  const nonRefundable = mealPlans.filter(({ cancellationPolicy }) => cancellationPolicy.type === 'NOT_REFUNDABLE');
  const refundable = mealPlans.filter(({ cancellationPolicy }) => cancellationPolicy.type === 'REFUNDABLE');
  const { t } = useTranslation();
  const { accommodationOnly, isTrain } = useUrlSearchParams();
  const { localization } = usePageWrapperData();
  const { country, lang } = localization;

  // To show different free cancellation dates in different blocks
  const refundableByDateArray = groupRefundableByUpToDate(refundable);

  return (
    <InformationContainer>
      {nonRefundable.length ? <CancellationPolicyContent type="NOT_REFUNDABLE" mealPlans={nonRefundable} /> : null}
      {refundableByDateArray.length
        ? refundableByDateArray.map((refundableByDateMealPlans, index) => (
            <CancellationPolicyContent
              key={refundableByDateMealPlans[0].id}
              type="REFUNDABLE"
              mealPlans={refundableByDateMealPlans}
              showDateInTitle={true}
              showTopDivider={nonRefundable.length > 0 || index > 0}
            />
          ))
        : null}
      {!accommodationOnly && (nonRefundable.length > 0 || refundableByDateArray.length > 0) && <SectionSeparator />}
      {!accommodationOnly && (
        <div
          css={`
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 8px;
          `}
        >
          <Icon
            category={isTrain ? 'Transportation' : 'Services'}
            name={isTrain ? 'icon-train' : 'icon-flight-ticket'}
            size={20}
            color="iconPrimary"
            alt=""
            css={`
              flex-shrink: 0;
            `}
          />
          <Text variant="Title5" forceTag="h3">
            {t(
              isTrain
                ? 'vacation.common.mealplan.room.cancelpolicy.train.title'
                : 'vacation.common.mealplan.room.cancelpolicy.flight.title'
            )}
          </Text>
        </div>
      )}
      {!accommodationOnly && (
        <SectionContainer>
          <Text variant="BodySmall">
            {t(
              isTrain
                ? 'vacation.common.mealplan.room.cancelpolicy.train.description'
                : 'vacation.common.mealplan.room.cancelpolicy.flight.description'
            )}
          </Text>
        </SectionContainer>
      )}

      {accommodationOnly === false && (nonRefundable.length > 0 || refundableByDateArray.length > 0) && (
        <Text variant="BodySmall" color="textSecondary">
          {country === Country.US && lang === Language.ES
            ? t('vacation.common.mealplan.room.cancelpolicy.disclaimer.es_us')
            : t('vacation.common.mealplan.room.cancelpolicy.disclaimer')}
        </Text>
      )}
    </InformationContainer>
  );
}

function CancellationPolicyContent({
  type,
  mealPlans,
  // TODO REV: unused while the section description is hidden — restore alongside the description block below.
  // hideDescription = false,
  showDateInTitle = false,
  showTopDivider = false,
}: {
  type: CancellationType;
  mealPlans: MealPlanType[];
  // TODO REV: restore alongside the description block below.
  // hideDescription?: boolean;
  showDateInTitle?: boolean;
  showTopDivider?: boolean;
}) {
  const { t } = useTranslation();
  const { accommodationOnly } = useUrlSearchParams();
  const { localization } = usePageWrapperData();
  const { locale } = localization;

  // Result-page copy for benefit labels (MPH-721) — the classic modal keeps the provider label.
  const cancellationPoliciesByRange = buildCancellationPoliciesByRange(
    mealPlans,
    t,
    accommodationOnly,
    getPromotionDisplayLabel
  );

  const cancellationPoliciesValuesSameRanges = Object.values(cancellationPoliciesByRange);
  const translationKeys = cancellationPoliciesTranslationKeys[type === 'UNKNOWN' ? 'NOT_REFUNDABLE' : type];

  return (
    <>
      {showTopDivider && <SectionSeparator />}
      <div
        css={`
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 8px;
        `}
      >
        <Icon
          category="Security and safety"
          name={type === 'REFUNDABLE' ? 'icon-shield' : 'icon-slashed-shield'}
          size={20}
          color="iconPrimary"
          alt=""
          css={`
            flex-shrink: 0;
          `}
        />
        <Text variant="Title5" forceTag="h3">
          {`${t(accommodationOnly ? translationKeys.titleHO : translationKeys.titleDP)}${showDateInTitle && mealPlans[0].cancellationPolicy.upTo ? ` ${t('vacation.common.mealplan.room.cancellationtype.refundable.before', { date: formatCancellationDate(getCancellationBeforeDate(mealPlans[0].cancellationPolicy.upTo), locale) })}` : ''}`}
        </Text>
      </div>
      <SectionContainer>
        {/* TODO REV: section description text hidden per design review — restore if confirmed.
        {!hideDescription && (
          <Text
            variant="BodySmall"
            css={`
              margin-bottom: 8px;
            `}
          >
            {t(translationKeys.description)}
          </Text>
        )}
        */}

        {cancellationPoliciesValuesSameRanges.map((cancellationPoliciesSameRange) => {
          const cancellationPoliciesValues = Object.values(cancellationPoliciesSameRange);

          return (
            <CancellationDatesContainer key={`ranges-${cancellationPoliciesValues[0].range.from}`}>
              {cancellationPoliciesValues.map(({ range, type: cancellationType, cancellationPolicies }, index) => {
                // Free-cancellation copy speaks "before X", where X is the day AFTER the last free day.
                const parsedCancellationDate =
                  cancellationType === 'FREE_CANCELLATION'
                    ? formatCancellationDate(getCancellationBeforeDate(range.to), locale)
                    : formatCancellationDate(range.from, locale);
                const showDiscounts = cancellationPolicies.filter((policy) => !policy.discount).length > 0;
                return (
                  <CancellationDateInfo key={range.from}>
                    {type === 'REFUNDABLE' && (
                      <div
                        css={`
                          display: flex;
                          flex-direction: column;
                          align-items: center;
                        `}
                      >
                        <TimelineDot />
                        {index < cancellationPoliciesValues.length - 1 && <VerticalDottedLine />}
                      </div>
                    )}
                    <div
                      css={`
                        display: flex;
                        flex-direction: column;
                        gap: 4px;
                        flex: 1;
                        min-width: 0;
                      `}
                    >
                      <Text
                        variant="Title6"
                        forceTag="h4"
                        css={`
                          display: flex;
                          align-items: center;
                          gap: 8px;
                        `}
                      >
                        {type === 'NOT_REFUNDABLE' && <TimelineDot />}
                        {cancellationType === 'FREE_CANCELLATION'
                          ? t('vacation.common.mealplan.room.cancelpolicy.datebefore', {
                              date: parsedCancellationDate,
                            })
                          : index < cancellationPoliciesValues.length - 1
                            ? t('vacation.select.cancellation_modal.title_from_to', {
                                date1: formatCancellationDate(range.from, locale),
                                date2: formatCancellationDate(range.to, locale),
                              })
                            : t('vacation.common.mealplan.room.cancelpolicy.dateafter', {
                                date: parsedCancellationDate,
                              })}
                      </Text>
                      <Text variant="BodySmall" css={type !== 'NOT_REFUNDABLE' ? `margin-bottom: 12px;` : undefined}>
                        {cancellationType === 'FREE_CANCELLATION'
                          ? accommodationOnly
                            ? t('vacation.common.mealplan.room.cancelpolicy.active.free.before', {
                                from_date: parsedCancellationDate,
                              })
                            : t('vacation.common.mealplan.room.cancelpolicy.active.dp').replaceAll(
                                '$from_date',
                                parsedCancellationDate
                              )
                          : index < cancellationPoliciesValues.length - 1
                            ? t('vacation.select.cancellation_modal.description.title_from_to', {
                                date1: formatCancellationDate(range.from, locale),
                                date2: formatCancellationDate(range.to, locale),
                              })
                            : t('vacation.common.mealplan.room.cancelpolicy.active.date', {
                                date: parsedCancellationDate,
                              })}
                      </Text>
                      {(type !== 'NOT_REFUNDABLE' || accommodationOnly) &&
                      (cancellationType !== 'FREE_CANCELLATION' || cancellationPoliciesValuesSameRanges.length > 1) ? (
                        <ul
                          css={css`
                            list-style: none;
                            width: 100%;
                            margin: 0;
                            padding: 12px 16px;
                            border-radius: ${(p) => p.theme.kallisto.border.radius.radius100};
                            background: ${(p) => p.theme.kallisto.color.background.static.backgroundLayer01};
                          `}
                        >
                          {cancellationPolicies.map((policy) => (
                            <li
                              key={`${policy.boardClass}${policy.discount ? `-${policy.discount}` : ''}`}
                              css={css`
                                display: flex;
                                padding: 8px 0;
                                align-items: baseline;
                                column-gap: 4px;
                                flex-wrap: wrap;

                                &:first-child {
                                  padding-top: 0;
                                }

                                &:last-child {
                                  padding-bottom: 0;
                                }

                                &:not(:last-child) {
                                  border-bottom: 1px dashed ${(p) => p.theme.commons.colors.gray100};
                                }
                              `}
                            >
                              <Text
                                variant="BodySmall"
                                css={`
                                  white-space: nowrap;
                                `}
                              >
                                {policy.boardClass}
                              </Text>
                              {showDiscounts && policy.discount ? (
                                <Text
                                  variant="BodySmall"
                                  css={css`
                                    font-size: ${(p) => p.theme.commons.font.sizes.small};
                                    white-space: nowrap;
                                  `}
                                >
                                  ({policy.discount})
                                </Text>
                              ) : null}

                              <Text
                                variant="Body"
                                css={css`
                                  flex-grow: 1;
                                `}
                              />
                              <Text variant="BodySmall">
                                {formatPrice({ price: policy?.amount ?? 0, localization })}
                              </Text>
                            </li>
                          ))}
                        </ul>
                      ) : undefined}
                    </div>
                  </CancellationDateInfo>
                );
              })}
              <Separator />
            </CancellationDatesContainer>
          );
        })}
      </SectionContainer>
    </>
  );
}
